/**
 * contentValidators/distractorChecks.ts
 * Validates distractor quality: presence, length, generic text, jargon,
 * misconception references, and D-QUAL heuristics (numeric scale, implausible
 * spelling, general outlier detection).
 */

import type { ValidationIssue } from '../../issueContract';
import { coach, info, blocker } from '../../issueContract';
import { getPolicy, getMinDistractorLength, getDistractorPattern } from '../../policyEngine';
import { isGenericText, isDidactic, GENERIC_DISTRACTOR_PATTERNS, FORBIDDEN_OPENER_PATTERNS, detectJargon, NON_MC_TYPES } from '../../../utils';
import type { Question } from '../../../../content';
import { STAGE } from './shared';

/**
 * Validate distractor quality for a multiple-choice question.
 */
export function validateDistractorQuality(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // BUG-6 fix: non-MC types skip distractor analysis
  if (NON_MC_TYPES.includes(question.questionType) || !question.options) {
    return issues;
  }
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';
  const tier = question.tier ?? 1;
  const module = question.module;
  const feedbackMap = question.feedback?.distractorExplanations ?? {};
  const optionCount = question.options.length;

  // Check all options have explanations
  const policy = getPolicy();
  if (policy.featureFlags.enforceDistractorExplanations) {
    for (let i = 0; i < optionCount; i++) {
      const exp = feedbackMap[String(i)];
      if (!exp || typeof exp !== 'string' || exp.trim() === '') {
        // Tier ≥ 9: blocker — high-stakes content must have distractor explanations
        if (tier >= 9) {
          issues.push(blocker('content', STAGE, 'CONTENT_MISSING_DISTRACTOR_EXPLANATION', `Missing distractor explanation for option ${i} (tier ${tier} — verplicht)`, {
            field: `feedback.distractorExplanations.${i}`,
            questionId: qId,
            familyId: fId,
            severity: 70
          }));
        } else {
          issues.push(coach('content', STAGE, 'CONTENT_MISSING_DISTRACTOR_EXPLANATION', `Missing distractor explanation for option ${i}`, {
            field: `feedback.distractorExplanations.${i}`,
            questionId: qId,
            familyId: fId,
            severity: tier >= 5 ? 55 : 45
          }));
        }
      }
    }
  }

  // Check distractor quality
  const minDistractor = getMinDistractorLength(tier);
  for (const [idx, exp] of Object.entries(feedbackMap)) {
    if (typeof exp !== 'string' || exp.trim() === '') continue;
    const isCorrectAnswer = Number(idx) === question.correctAnswer;

    // Length check (applies to all, including correct answer)
    if (exp.trim().length < minDistractor) {
      issues.push(info('content', STAGE, 'CONTENT_DISTRACTOR_TOO_SHORT', `Distractor explanation for option ${idx} (${exp.trim().length} chars) below minimum ${minDistractor}`, {
        field: `feedback.distractorExplanations.${idx}`,
        questionId: qId,
        familyId: fId,
        severity: 15
      }));
    }

    // Forbidden opener check on distractors
    if (!isCorrectAnswer) {
      for (const {
        pattern,
        label
      } of FORBIDDEN_OPENER_PATTERNS) {
        if (pattern.test(exp.trim())) {
          issues.push(coach('content', STAGE, 'CONTENT_FORBIDDEN_OPENER', `Distractor explanation for option ${idx} starts with forbidden opener "${label}"`, {
            field: `feedback.distractorExplanations.${idx}`,
            actual: label,
            questionId: qId,
            familyId: fId,
            severity: 40,
            suggestion: `Remove the "${label}" opener — explain the misconception directly`
          }));
        }
      }
    }

    // Generic check — skip for correct answer
    if (!isCorrectAnswer && isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)) {
      issues.push(coach('content', STAGE, 'CONTENT_GENERIC_DISTRACTOR', `Distractor explanation for option ${idx} is generic — must name the specific misconception`, {
        field: `feedback.distractorExplanations.${idx}`,
        questionId: qId,
        familyId: fId,
        severity: 45,
        suggestion: 'Explain WHY this answer is wrong and what misconception it represents'
      }));
    }

    // Jargon check on distractor text
    const distractorJargon = detectJargon(exp, tier);
    for (const {
      term,
      replacement
    } of distractorJargon) {
      issues.push(info('content', STAGE, 'CONTENT_JARGON_DETECTED', `Jargon "${term}" in distractor explanation for option ${idx}`, {
        field: `feedback.distractorExplanations.${idx}`,
        actual: term,
        questionId: qId,
        familyId: fId,
        severity: 30,
        suggestion: `Replace "${term}" with ${replacement}`
      }));
    }
  }

  // Misconception reference check
  if (module) {
    const misconceptionPattern = getDistractorPattern(module);
    if (misconceptionPattern) {
      const wrongEntries = Object.entries(feedbackMap).filter(([idx]) => Number(idx) !== question.correctAnswer);
      if (wrongEntries.length > 0) {
        const hasMisconceptionRef = wrongEntries.some(([, exp]) => typeof exp === 'string' && (misconceptionPattern.test(exp) || isDidactic(exp) || /je dacht (waarschijnlijk|misschien)/i.test(exp) || exp.trim().length >= 80 && !isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)));
        if (!hasMisconceptionRef) {
          issues.push(coach('content', STAGE, 'CONTENT_NO_MISCONCEPTION_REF', `No distractor explanation references a ${module}-specific misconception`, {
            field: 'feedback.distractorExplanations',
            questionId: qId,
            familyId: fId,
            severity: 40
          }));
        }
      }
    }
  }

  // ── D-QUAL-1: Numeric scale mismatch ────────────────────────────────────
  {
    const correctIdx = question.correctAnswer;
    const opts = question.options ?? [];
    if (typeof correctIdx === 'number' && opts.length >= 3) {
      const parseNumericOption = (opt: string): number | null => {
        const cleaned = opt.replace(/[€$£\s]/g, '').replace(/\./g, '').replace(',', '.').replace(/[a-zA-Z%°²³]+$/g, '').trim();
        const num = parseFloat(cleaned);
        return isNaN(num) ? null : num;
      };
      const correctVal = parseNumericOption(opts[correctIdx] ?? '');
      const distractorVals: number[] = [];
      for (let i = 0; i < opts.length; i++) {
        if (i === correctIdx) continue;
        const val = parseNumericOption(opts[i]);
        if (val !== null) distractorVals.push(val);
      }
      if (correctVal !== null && correctVal !== 0 && distractorVals.length >= 2) {
        const avgDistractor = distractorVals.reduce((a, b) => a + b, 0) / distractorVals.length;
        const ratio = avgDistractor !== 0 ? Math.abs(correctVal) / Math.abs(avgDistractor) : Infinity;
        if (ratio > 2.5 || ratio < 0.4) {
          const distractorStdDev = Math.sqrt(distractorVals.reduce((sum, v) => sum + Math.pow(v - avgDistractor, 2), 0) / distractorVals.length);
          const clusterTight = Math.abs(avgDistractor) > 0.01 ? distractorStdDev / Math.abs(avgDistractor) < 0.3 : true;
          if (clusterTight) {
            issues.push(coach('content', STAGE, 'CONTENT_WEAK_DISTRACTOR_SCALE', `Numeric scale mismatch: correct answer (${opts[correctIdx]}) is ${ratio > 1 ? ratio.toFixed(1) + '× larger' : (1 / ratio).toFixed(1) + '× smaller'} than all distractors (avg ${avgDistractor.toFixed(1)}) — children can guess by magnitude alone`, {
              field: 'options',
              expected: 'Distractors in same magnitude range as correct answer',
              actual: `correct=${correctVal}, distractors avg=${avgDistractor.toFixed(1)}`,
              questionId: qId,
              familyId: fId,
              severity: 55,
              suggestion: 'Make distractors represent plausible wrong calculations at the same scale as the correct answer (e.g., common arithmetic errors, off-by-one in steps)'
            }));
          }
        }
      }
    }
  }

  // ── D-QUAL-2: Implausible spelling/text patterns in options ─────────────
  {
    const isLanguageModule = module === 'SpellingRegel' || module === 'SpellingWW' || module === 'Taalbegrip';
    if (isLanguageModule) {
      const opts = question.options ?? [];
      const implausibleOptions: string[] = [];
      for (let i = 0; i < opts.length; i++) {
        if (i === question.correctAnswer) continue;
        const opt = opts[i];
        if (!opt) continue;
        const words = opt.split(/\s+/);
        for (const word of words) {
          const cleaned = word.replace(/^["""''„«»(]+/, '').replace(/["""''„«»).,!?;:]+$/, '');
          if (cleaned.length < 2) continue;

          // Check 1: Mixed case WITHIN a word (not at start)
          const innerPart = cleaned.slice(1);
          if (/[A-Z]/.test(innerPart) && !/^[A-Z]+$/.test(cleaned) && !/^[a-z]+$/.test(cleaned)) {
            const upperInMiddle = innerPart.match(/[A-Z]/g);
            if (upperInMiddle && upperInMiddle.length > 0) {
              implausibleOptions.push(`"${opt}" has mixed case ("${cleaned}") — visually obvious giveaway`);
              break;
            }
          }

          // Check 2: Impossible Dutch consonant clusters at word end
          if (/[dt]{3,}$/i.test(cleaned)) {
            implausibleOptions.push(`"${opt}" has impossible ending ("${cleaned}") — triple d/t cluster`);
            break;
          }

          // Check 3: Repeated identical consonant triples
          if (/(.)\1{2,}/i.test(cleaned) && /[bcdfghjklmnpqrstvwxyz]/i.test(cleaned.match(/(.)\1{2,}/i)?.[1] ?? '')) {
            implausibleOptions.push(`"${opt}" has triple letter ("${cleaned}") — visually implausible`);
            break;
          }

          // Check 4: Apostrophe in impossible position
          if (/[a-z]'[a-z]/i.test(cleaned) && !/^(m'n|z'n|d'r|'[st])/.test(cleaned) && !/s$/.test(cleaned.split("'")[0])) {
            const beforeApostrophe = cleaned.split("'")[0];
            const afterApostrophe = cleaned.split("'")[1];
            if (beforeApostrophe && afterApostrophe && afterApostrophe.length > 1) {
              implausibleOptions.push(`"${opt}" has unusual apostrophe ("${cleaned}") — likely implausible`);
              break;
            }
          }
        }
      }
      if (implausibleOptions.length > 0) {
        issues.push(coach('content', STAGE, 'CONTENT_WEAK_DISTRACTOR_IMPLAUSIBLE', `${implausibleOptions.length} distractor(s) have visually implausible patterns: ${implausibleOptions.slice(0, 2).join('; ')}`, {
          field: 'options',
          questionId: qId,
          familyId: fId,
          severity: 50,
          suggestion: 'Distractors should look like real words/spellings that represent actual misconceptions — not obviously impossible letter combinations that children can eliminate by sight'
        }));
      }
    }
  }

  // ── D-QUAL-3: General distractor outlier detection ──────────────────────
  {
    const correctIdx = question.correctAnswer;
    const opts = question.options ?? [];
    const mcLikeTypes: string[] = ['multiple_choice', 'true_false_series', 'comparison_judge'];
    const isMcLike = mcLikeTypes.includes(question.questionType) || opts.length >= 3 && typeof correctIdx === 'number';
    if (typeof correctIdx === 'number' && opts.length >= 3 && isMcLike) {
      const correctOpt = (opts[correctIdx] ?? '').trim();
      const distractorOpts = opts.filter((_, i) => i !== correctIdx).map(o => (o ?? '').trim());
      const outlierReasons: string[] = [];

      // ── Sub-check A: Length outlier ────────────────────────────────────
      {
        const correctLen = correctOpt.length;
        const distractorLens = distractorOpts.map(d => d.length);
        const avgDistractorLen = distractorLens.reduce((a, b) => a + b, 0) / distractorLens.length;
        const minDistractorLen = Math.min(...distractorLens);
        const maxDistractorLen = Math.max(...distractorLens);
        if (correctLen > 0 && avgDistractorLen > 0) {
          const lenRatio = correctLen / avgDistractorLen;
          const outsideRange = correctLen < minDistractorLen * 0.5 || correctLen > maxDistractorLen * 2.0;
          const absDiff = Math.abs(correctLen - avgDistractorLen);
          if (outsideRange && absDiff > 8 && (lenRatio < 0.4 || lenRatio > 2.5)) {
            outlierReasons.push(`length outlier: correct=${correctLen} chars, distractors avg=${Math.round(avgDistractorLen)} chars`);
          }
        }
      }

      // ── Sub-check B: Numeric one-sided spread ─────────────────────────
      {
        const parseNum = (s: string): number | null => {
          const cleaned = s.replace(/[€$£\s]/g, '').replace(/\./g, '').replace(',', '.').replace(/[a-zA-Z%°²³]+$/g, '').trim();
          const n = parseFloat(cleaned);
          return isNaN(n) ? null : n;
        };
        const correctNum = parseNum(correctOpt);
        const distractorNums = distractorOpts.map(parseNum).filter((n): n is number => n !== null);
        if (correctNum !== null && distractorNums.length >= 2 && distractorNums.length === distractorOpts.length) {
          const allHigher = distractorNums.every(d => d > correctNum);
          const allLower = distractorNums.every(d => d < correctNum);
          if (allHigher || allLower) {
            outlierReasons.push(`numeric spread: all distractors are ${allHigher ? 'higher' : 'lower'} than correct (${correctNum})`);
          }
        }
      }

      // ── Sub-check C: Format outlier ───────────────────────────────────
      {
        const formatSignature = (s: string): string => {
          const parts: string[] = [];
          if (/^[€$£]/.test(s)) parts.push('currency-prefix');
          if (/[€$£]$/.test(s)) parts.push('currency-suffix');
          if (/\d,\d/.test(s)) parts.push('comma-decimal');
          if (/\d\.\d/.test(s)) parts.push('dot-decimal');
          if (/[²³]/.test(s) || /\^/.test(s)) parts.push('superscript');
          if (/[a-zA-Z]{1,5}$/.test(s.trim())) parts.push('unit-suffix');
          if (/^\(.*\)$/.test(s.trim())) parts.push('parenthesized');
          if (/^-/.test(s.trim())) parts.push('negative');
          if (/\\frac/.test(s)) parts.push('latex-fraction');
          if (/\d/.test(s)) parts.push('has-number');else parts.push('text-only');
          return parts.sort().join('|');
        };
        const correctFmt = formatSignature(correctOpt);
        const distractorFmts = distractorOpts.map(formatSignature);
        const allDistractorsSameFormat = distractorFmts.every(f => f === distractorFmts[0]);
        if (allDistractorsSameFormat && distractorFmts[0] !== correctFmt && distractorOpts.length >= 2) {
          outlierReasons.push(`format outlier: correct has format [${correctFmt}], all distractors have [${distractorFmts[0]}]`);
        }
      }

      // ── Sub-check D: Distractor similarity clustering ─────────────────
      {
        const bigrams = (s: string): Set<string> => {
          const lower = s.toLowerCase().replace(/\s+/g, '');
          const bg = new Set<string>();
          for (let i = 0; i < lower.length - 1; i++) {
            bg.add(lower.slice(i, i + 2));
          }
          return bg;
        };
        const dice = (a: Set<string>, b: Set<string>): number => {
          if (a.size === 0 && b.size === 0) return 1;
          if (a.size === 0 || b.size === 0) return 0;
          let overlap = 0;
          for (const bg of a) {
            if (b.has(bg)) overlap++;
          }
          return 2 * overlap / (a.size + b.size);
        };
        if (correctOpt.length > 3 && distractorOpts.every(d => d.length > 3)) {
          const correctBigrams = bigrams(correctOpt);
          const distractorBigramSets = distractorOpts.map(bigrams);
          let pairCount = 0;
          let pairSimilaritySum = 0;
          for (let i = 0; i < distractorBigramSets.length; i++) {
            for (let j = i + 1; j < distractorBigramSets.length; j++) {
              pairSimilaritySum += dice(distractorBigramSets[i], distractorBigramSets[j]);
              pairCount++;
            }
          }
          const avgDistractorSimilarity = pairCount > 0 ? pairSimilaritySum / pairCount : 0;
          const avgCorrectToDistractor = distractorBigramSets.reduce((sum, dBg) => sum + dice(correctBigrams, dBg), 0) / distractorBigramSets.length;
          if (avgDistractorSimilarity > 0.65 && avgCorrectToDistractor < 0.35) {
            outlierReasons.push(`similarity cluster: distractors are ${(avgDistractorSimilarity * 100).toFixed(0)}% similar to each other but only ${(avgCorrectToDistractor * 100).toFixed(0)}% to correct answer`);
          }
        }
      }

      // Emit a single issue if any outlier sub-check triggered
      if (outlierReasons.length > 0) {
        issues.push(coach('content', STAGE, 'CONTENT_WEAK_DISTRACTOR_OUTLIER', `Correct answer stands out from distractors (${outlierReasons.length} signal${outlierReasons.length > 1 ? 's' : ''}): ${outlierReasons.join('; ')}`, {
          field: 'options',
          expected: 'Correct answer should blend in with distractors in length, format, numeric range, and visual similarity',
          actual: outlierReasons.join('; '),
          questionId: qId,
          familyId: fId,
          severity: 50,
          suggestion: 'Redesign distractors so the correct answer does not visually stand out — vary distractor lengths, bracket the correct numeric value, match formatting, and ensure distractors are not all clones of each other'
        }));
      }
    }
  }
  return issues;
}